import { FC, useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { authService, PendingUser } from '@/services/auth.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminUser } from '@/types/admin';

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  ADMIN: { label: 'Admin', color: 'bg-red-500/10 text-red-400' },
  CURATOR: { label: 'Curador', color: 'bg-purple-500/10 text-purple-400' },
  LEARNER: { label: 'Aprendiz', color: 'bg-green-500/10 text-green-400' },
};

type Tab = 'all' | 'pending';

const AdminUsersPage: FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: Tab = searchParams.get('status') === 'pending' ? 'pending' : 'all';
  const [tab, setTab] = useState<Tab>(initialTab);

  const [users, setUsers] = useState<AdminUser[]>([]);
  // A listagem é paginada de verdade: `total` é quantos existem no filtro atual
  // (não quantos estão na tela) e `hasMore` diz se ainda há o que carregar.
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Rejection modal state
  const [rejectTarget, setRejectTarget] = useState<PendingUser | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Keep ?status=pending in URL synced with tab selection
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'pending') next.set('status', 'pending');
    else next.delete('status');
    setSearchParams(next, { replace: true });
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFilters = useCallback(() => {
    const filters: { role?: string; is_active?: string; search?: string } = {};
    if (roleFilter) filters.role = roleFilter;
    if (statusFilter) filters.is_active = statusFilter;
    if (searchDebounced) filters.search = searchDebounced;
    return filters;
  }, [roleFilter, statusFilter, searchDebounced]);

  const loadUsers = useCallback(async () => {
    if (tab !== 'all') return;
    setIsLoading(true);
    try {
      const resp = await adminService.getUsers({ ...currentFilters(), page: 1 });
      setUsers(resp.results);
      setTotal(resp.count);
      setHasMore(Boolean(resp.next));
      setPage(1);
    } catch {
      /* silently fail */
    } finally {
      setIsLoading(false);
    }
  }, [tab, currentFilters]);

  /** Acrescenta a próxima página à lista já na tela (não substitui). */
  const loadMoreUsers = useCallback(async () => {
    const proxima = page + 1;
    setIsLoadingMore(true);
    try {
      const resp = await adminService.getUsers({ ...currentFilters(), page: proxima });
      setUsers((atuais) => [...atuais, ...resp.results]);
      setTotal(resp.count);
      setHasMore(Boolean(resp.next));
      setPage(proxima);
    } catch {
      /* silently fail */
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, currentFilters]);

  const loadPending = useCallback(async () => {
    try {
      const { count, results } = await authService.getPendingUsers(
        searchDebounced ? { search: searchDebounced } : undefined,
      );
      setPendingCount(count);
      setPendingUsers(results);
    } catch {
      /* silently fail */
    }
  }, [searchDebounced]);

  const loadPendingCountOnly = useCallback(async () => {
    try {
      const { count } = await authService.getPendingUsers();
      setPendingCount(count);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (tab === 'all') {
      loadUsers();
      loadPendingCountOnly();
    } else {
      setIsLoading(true);
      loadPending().finally(() => setIsLoading(false));
    }
  }, [tab, loadUsers, loadPending, loadPendingCountOnly]);

  const handleToggleActive = async (user: AdminUser) => {
    try {
      await adminService.toggleActive(user.id);
      await loadUsers();
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      showToast(error?.response?.data?.error || 'Erro ao alterar status', 'error');
    }
  };

  const handleResetPassword = async (user: AdminUser) => {
    const password = prompt(`Nova senha para ${user.name}:`);
    if (!password) return;
    try {
      await adminService.resetPassword(user.id, password);
      showToast('Senha alterada com sucesso!', 'success');
    } catch {
      showToast('Erro ao resetar senha', 'error');
    }
  };

  const handleApprove = async (user: PendingUser) => {
    if (!window.confirm(`Aprovar acesso de ${user.name} (matrícula ${user.matricula})?`)) return;
    try {
      await authService.approvePendingUser(user.id);
      showToast(`${user.name} aprovado com sucesso.`, 'success');
      await loadPending();
    } catch {
      showToast('Não foi possível aprovar. Tente novamente.', 'error');
    }
  };

  const openRejectModal = (user: PendingUser) => {
    setRejectTarget(user);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setRejectSubmitting(true);
    try {
      await authService.rejectPendingUser(rejectTarget.id, rejectReason.trim() || undefined);
      showToast(`${rejectTarget.name} rejeitado.`, 'success');
      setRejectTarget(null);
      await loadPending();
    } catch {
      showToast('Não foi possível rejeitar. Tente novamente.', 'error');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Usuários</h1>
        <Link
          to="/admin/usuarios/novo"
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-xl">person_add</span>
          Novo Usuário
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-theme-border">
        <button
          type="button"
          data-testid="tab-all"
          onClick={() => setTab('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'all'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-muted hover:text-content-primary'
          }`}
        >
          Todos os usuários
        </button>
        <button
          type="button"
          data-testid="tab-pending"
          onClick={() => setTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            tab === 'pending'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-muted hover:text-content-primary'
          }`}
        >
          Aguardando aprovação
          {pendingCount > 0 && (
            <span data-testid="pending-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder={tab === 'pending' ? 'Buscar por nome ou matrícula…' : 'Buscar por nome ou email...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-surface-card border border-theme-border rounded-lg px-4 py-2 text-sm text-content-primary placeholder-content-muted min-w-[250px]"
        />
        {tab === 'all' && (
          <>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-surface-card border border-theme-border rounded-lg px-4 py-2 text-sm text-content-primary"
            >
              <option value="">Todos os tipos</option>
              <option value="ADMIN">Admin</option>
              <option value="CURATOR">Curador</option>
              <option value="LEARNER">Aprendiz</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-surface-card border border-theme-border rounded-lg px-4 py-2 text-sm text-content-primary"
            >
              <option value="">Todos os status</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </>
        )}
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : tab === 'all' ? (
        users.length === 0 ? (
          <p className="text-content-muted">Nenhum usuário encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                  <th className="pb-3 font-medium">Nome</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium text-center">Tipo</th>
                  <th className="pb-3 font-medium">Setor</th>
                  <th className="pb-3 font-medium text-center">XP</th>
                  <th className="pb-3 font-medium text-center">Nível</th>
                  <th className="pb-3 font-medium text-center">Status</th>
                  <th className="pb-3 font-medium text-center">Última Atividade</th>
                  <th className="pb-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const roleInfo = ROLE_LABELS[user.role] || { label: user.role, color: 'bg-gray-500/10 text-gray-400' };
                  return (
                    <tr key={user.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                      <td className="py-4 font-medium">{user.name}</td>
                      <td className="py-4 text-content-muted text-sm">{user.email}</td>
                      <td className="py-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="py-4 text-sm">{user.sector?.name || '-'}</td>
                      <td className="py-4 text-center text-sm">{user.total_xp.toLocaleString()}</td>
                      <td className="py-4 text-center text-sm">{user.level}</td>
                      <td className="py-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          user.is_active
                            ? 'bg-green-500/10 text-green-400'
                            : 'bg-red-500/10 text-red-400'
                        }`}>
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="py-4 text-center text-sm text-content-muted">
                        {formatDate(user.last_seen_at)}
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/admin/usuarios/${user.id}/editar`}
                            className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                            title="Editar"
                          >
                            <span className="material-symbols-outlined text-xl">edit</span>
                          </Link>
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                            title="Resetar Senha"
                          >
                            <span className="material-symbols-outlined text-xl">lock_reset</span>
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
                            title={user.is_active ? 'Desativar' : 'Ativar'}
                          >
                            <span className="material-symbols-outlined text-xl">
                              {user.is_active ? 'person_off' : 'person'}
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between gap-4 py-4">
              <p className="text-sm text-content-muted" data-testid="users-count">
                Mostrando {users.length} de {total}
              </p>
              {hasMore && (
                <button
                  onClick={loadMoreUsers}
                  disabled={isLoadingMore}
                  data-testid="load-more-users"
                  className="px-4 py-2 rounded-lg border border-theme-border text-sm text-content-primary hover:bg-surface-hover transition-colors disabled:opacity-50"
                >
                  {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
                </button>
              )}
            </div>
          </div>
        )
      ) : (
        // ======== Pending approvals tab ========
        pendingUsers.length === 0 ? (
          <div className="rounded-lg border border-theme-border bg-surface-card p-8 text-center">
            <span className="material-symbols-outlined text-4xl text-content-muted mb-2">inbox</span>
            <p className="text-content-muted">Nenhum cadastro pendente no momento.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="pending-users-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                  <th className="pb-3 font-medium">Nome</th>
                  <th className="pb-3 font-medium">Matrícula</th>
                  <th className="pb-3 font-medium">Setor</th>
                  <th className="pb-3 font-medium">Solicitado em</th>
                  <th className="pb-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((p) => (
                  <tr key={p.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                    <td className="py-4 font-medium">{p.name}</td>
                    <td className="py-4 text-sm font-mono">{p.matricula}</td>
                    <td className="py-4 text-sm">{p.sector?.name || '-'}</td>
                    <td className="py-4 text-sm text-content-muted">{formatDateTime(p.created_at)}</td>
                    <td className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(p)}
                          data-testid={`approve-${p.matricula}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 text-sm font-medium"
                        >
                          <span className="material-symbols-outlined text-lg">check</span>
                          Aprovar
                        </button>
                        <button
                          onClick={() => openRejectModal(p)}
                          data-testid={`reject-${p.matricula}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 text-sm font-medium"
                        >
                          <span className="material-symbols-outlined text-lg">close</span>
                          Rejeitar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
        >
          <div className="w-full max-w-md rounded-2xl border border-theme-border bg-surface-card p-6">
            <h2 className="text-lg font-semibold mb-2">Rejeitar cadastro</h2>
            <p className="text-sm text-content-muted mb-4">
              Você vai rejeitar o acesso de <strong>{rejectTarget.name}</strong> (matrícula <strong>{rejectTarget.matricula}</strong>).
              O motivo é opcional e será mostrado ao usuário caso ele tenha email.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motivo (opcional)"
              rows={3}
              data-testid="reject-reason-input"
              className="w-full rounded-lg border border-theme-border bg-surface-primary p-3 text-sm text-content-primary placeholder-content-muted"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setRejectTarget(null)}
                className="px-4 py-2 rounded-lg text-sm text-content-muted hover:text-content-primary"
                disabled={rejectSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={submitReject}
                disabled={rejectSubmitting}
                data-testid="confirm-reject"
                className="px-4 py-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 text-sm font-medium disabled:opacity-50"
              >
                {rejectSubmitting ? 'Rejeitando…' : 'Rejeitar cadastro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
